<template>
	<!-- Une carte par version du détail disponible : la lecture du document, l'analyse par modèle de
	     langage, les lignes enregistrées. Un clic bascule le tableau sur la version choisie ; chaque
	     carte en résume l'état pour qu'on puisse les comparer sans les ouvrir. -->
	<div class="invoice-supplier-versions">
		<div
			v-for="version in versions"
			:key="version.key"
			class="invoice-supplier-versions__card"
			:class="{
				'invoice-supplier-versions__card--current': version.key === current,
				'invoice-supplier-versions__card--ai': version.key === 'ai'
			}"
		>
			<button
				v-if="version.summary"
				type="button"
				class="invoice-supplier-versions__main"
				:aria-pressed="version.key === current ? 'true' : 'false'"
				@click="$emit('select', version.key)"
			>
				<span class="invoice-supplier-versions__title">
					<font-awesome-icon v-if="version.key === 'ai'" :icon="ai_icon" />
					{{ version.title }}
					<b-badge v-if="version.dirty" variant="info">{{ $t('invoice.supplier_details_version_modified') }}</b-badge>
				</span>
				<span v-if="!version.summary.count" class="invoice-supplier-versions__sub text-danger">{{ $t('invoice.supplier_details_version_empty') }}</span>
				<template v-else>
					<span class="invoice-supplier-versions__sub">
						{{ $t('invoice.supplier_details_version_lines', { count: version.summary.count }) }} ·
						<span v-if="version.summary.balanced === true" class="text-success">{{ $t('invoice.supplier_details_version_balanced') }}</span>
						<span v-else-if="version.summary.balanced === false" class="text-danger">{{ $t('invoice.supplier_details_version_unbalanced') }}</span>
					</span>
					<span class="invoice-supplier-versions__sub">
						{{ $t('invoice.supplier_details_version_horses', { count: version.summary.horses, total: version.summary.count }) }} ·
						{{ $t('invoice.supplier_details_version_dated', { count: version.summary.dated, total: version.summary.count }) }}
					</span>
				</template>
				<span v-if="version.key === 'ai' && ai.task" class="invoice-supplier-versions__sub">{{ providerLabel(ai.task.aitask_provider) }}</span>
			</button>

			<div v-else class="invoice-supplier-versions__main">
				<span class="invoice-supplier-versions__title">
					<font-awesome-icon :icon="ai_icon" /> {{ version.title }}
				</span>
				<span class="invoice-supplier-versions__sub">{{ $t('invoice.supplier_details_ai_intro') }}</span>
			</div>

			<!-- Le pied de la carte de l'analyse porte son lancement, son avancement ou son échec. -->
			<div v-if="version.key === 'ai'" class="invoice-supplier-versions__foot">
				<template v-if="ai.running">
					<span>
						<font-awesome-icon :icon="['fal', 'spinner']" spin />
						{{ $t(ai.running.aitask_status === 'queued' ? 'invoice.supplier_details_ai_queued' : 'invoice.supplier_details_ai_running') }}
						· {{ providerLabel(ai.running.aitask_provider) }}
					</span>
					<span class="invoice-supplier-details__hint">{{ $t('invoice.supplier_details_ai_background') }}</span>
				</template>

				<template v-else-if="ai.failed && !relaunch">
					<span class="text-danger">{{ $t('invoice.supplier_details_ai_failed', { error: errorLabel(ai.failed.aitask_error) }) }}</span>
					<div class="invoice-supplier-versions__launch">
						<button type="button" class="btn btn-secondary btn-sm" @click="$emit('retry')">{{ $t('invoice.supplier_details_ai_retry') }}</button>
						<a href="#" @click.prevent="relaunch = true">{{ $t('invoice.supplier_details_ai_relaunch_other') }}</a>
					</div>
				</template>

				<template v-else-if="!ai.task || relaunch">
					<div class="invoice-supplier-versions__launch">
						<b-form-select v-model="provider" size="sm" :options="provider_options" />
						<button type="button" class="btn btn-primary btn-sm" :disabled="!provider" @click="launch">
							<font-awesome-icon :icon="ai_icon" />
							{{ $t(ai.task ? 'invoice.supplier_details_ai_relaunch' : 'invoice.supplier_details_ai_launch') }}
						</button>
					</div>
					<span v-if="provider === ai.last" class="invoice-supplier-details__hint">{{ $t('invoice.supplier_details_ai_last_model') }}</span>
				</template>

				<a v-else href="#" @click.prevent="relaunch = true">{{ $t('invoice.supplier_details_ai_relaunch_other') }}</a>
			</div>
		</div>
	</div>
</template>


<script type="text/javascript">
	// Importée directement plutôt que déclarée dans la bibliothèque d'icônes partagée de groomy-root :
	// elle ne sert qu'ici.
	import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles'

	export default {
		name: "InvoiceSupplierLinesVersions",

		props: {
			// [{ key, title, dirty, summary: { count, balanced, horses, dated } | null }]
			versions: { type: Array, default: () => [] },
			current: { type: String, default: null },
			// { providers: [{ key, label }], last, task, running, failed }
			ai: { type: Object, default: () => ({ providers: [], last: null, task: null, running: null, failed: null }) }
		},

		data() {
			return {
				// Fournisseur choisi pour le prochain lancement, préréglé sur le dernier utilisé.
				provider: null,
				// Le sélecteur de modèle est rouvert sur une analyse déjà disponible ou en échec.
				relaunch: false
			}
		},

		computed: {
			/**
			 * Icône de l'analyse, importée à part : elle ne sert qu'ici.
			 */
			ai_icon() {
				return faSparkles
			},

			/**
			 * Fournisseurs connectés, tels que la liste les propose.
			 */
			provider_options() {
				return this.ai.providers.map(provider => ({ value: provider.key, text: provider.label }))
			}
		},

		watch: {
			'ai.providers': {
				immediate: true,
				handler(providers) {
					if(this.provider || !providers.length) return

					this.provider = this.ai.last || providers[0].key
				}
			}
		},

		methods: {
			/**
			 * Lance l'analyse avec le fournisseur choisi, et referme le choix du modèle.
			 */
			launch() {
				this.relaunch = false
				this.$emit('launch', this.provider)
			},

			/**
			 * Nom d'un fournisseur, à défaut sa clé.
			 */
			providerLabel(key) {
				const provider = this.ai.providers.find(candidate => candidate.key === key)

				return provider ? provider.label : key
			},

			/**
			 * Motif d'échec rendu par le serveur : un code connu se traduit, le message d'un fournisseur
			 * est montré tel qu'il l'a écrit.
			 */
			errorLabel(error) {
				const key = 'invoice.supplier_details_ai_errors.' + error

				return this.$i18n.keyExists(key) ? this.$t(key) : error
			}
		}
	}
</script>
